/**
 * Feature 12 — Client 5-step setup wizard.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authService } from '../../services/auth';
import { clientApi } from '../../services/client';
import { useAuthStore } from '../../stores/authStore';


function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">
      Step {current} of {total}
    </div>
  );
}


export default function ClientOnboarding() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);

  const [step, setStep] = useState(1);
  const [createdSiteId, setCreatedSiteId] = useState<string | null>(null);

  // Step 1 — confirm org details.
  const { data: orgRes } = useQuery({
    queryKey: ['client', 'organization'],
    queryFn: () => clientApi.getOrganization().then((r) => r.data),
  });
  const [orgFields, setOrgFields] = useState({
    name: '', city: '', state: '', address: '', phone: '',
  });
  const [orgLoaded, setOrgLoaded] = useState(false);
  if (orgRes && !orgLoaded) {
    setOrgFields({
      name: orgRes.name || '',
      city: orgRes.city || '',
      state: orgRes.state || '',
      address: orgRes.address || '',
      phone: orgRes.phone || '',
    });
    setOrgLoaded(true);
  }
  const orgUpdate = useMutation({
    mutationFn: () => clientApi.updateOrganization(orgFields),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', 'organization'] });
      setStep(2);
    },
  });

  // Step 2 — first site (skippable).
  const [site, setSite] = useState({
    name: '', address: '', city: '', state: '', zip_code: '', timezone: 'America/New_York',
  });
  const siteCreate = useMutation({
    mutationFn: () => clientApi.createSite(site).then((r) => r.data),
    onSuccess: (data) => {
      setCreatedSiteId(data.id);
      setStep(3);
    },
  });

  // Step 3 — site context (skippable). Inline minimal form using F4 schema.
  const [ctx, setCtx] = useState({
    site_type: '', coverage_type: '', coverage_hours: '', patrol_intensity: '',
  });
  const ctxSave = useMutation({
    mutationFn: async () => {
      if (!createdSiteId) return;
      await clientApi.updateSiteContext(createdSiteId, ctx);
    },
    onSuccess: () => setStep(4),
  });

  // Step 4 — browse / invite (skippable).
  const finishMutation = useMutation({
    mutationFn: () => authService.completeSetup(),
    onSuccess: (data) => {
      const current = useAuthStore.getState().user;
      if (current) {
        setUser({ ...current, setup_completed_at: data.setup_completed_at });
      }
      navigate('/client', { replace: true });
    },
  });

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center px-4 py-16">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        <StepIndicator current={step} total={5} />

        {step === 1 && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900">Welcome to GuardGrade</h1>
            <p className="mt-2 text-sm text-slate-600">
              Confirm your organization details so your account reflects
              the right information.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(['name', 'city', 'state', 'address', 'phone'] as const).map((f) => (
                <label key={f} className={f === 'name' || f === 'address' ? 'sm:col-span-2' : ''}>
                  <span className="text-xs font-medium text-slate-600 capitalize">{f}</span>
                  <input
                    value={orgFields[f] || ''}
                    onChange={(e) => setOrgFields({ ...orgFields, [f]: e.target.value })}
                    className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                  />
                </label>
              ))}
            </div>
            <div className="mt-6">
              <button
                onClick={() => orgUpdate.mutate()}
                disabled={orgUpdate.isPending || !orgFields.name.trim()}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-brand-600"
              >
                {orgUpdate.isPending ? 'Saving…' : 'Continue'}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900">Add your first site</h1>
            <p className="mt-2 text-sm text-slate-600">
              You can add more later from the Sites page.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(['name', 'address', 'city', 'state', 'zip_code'] as const).map((f) => (
                <label key={f} className={f === 'name' || f === 'address' ? 'sm:col-span-2' : ''}>
                  <span className="text-xs font-medium text-slate-600 capitalize">{f.replace('_', ' ')}</span>
                  <input
                    value={(site as Record<string, string>)[f] || ''}
                    onChange={(e) => setSite({ ...site, [f]: e.target.value })}
                    className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                  />
                </label>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={() => siteCreate.mutate()}
                disabled={siteCreate.isPending || !site.name.trim()}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-brand-600"
              >
                {siteCreate.isPending ? 'Adding…' : 'Add Site'}
              </button>
              <button onClick={() => setStep(5)} className="text-sm text-slate-500 underline hover:text-slate-700">
                Skip for now
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900">Tell us about the site</h1>
            <p className="mt-2 text-sm text-slate-600">
              Context helps GuardGrade score patrol activity fairly.  You can
              come back and finish this from the site detail page later.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label>
                <span className="text-xs font-medium text-slate-600">Site type</span>
                <select
                  value={ctx.site_type}
                  onChange={(e) => setCtx({ ...ctx, site_type: e.target.value })}
                  className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                >
                  <option value="">—</option>
                  <option value="hotel">Hotel</option>
                  <option value="apartment">Apartment</option>
                  <option value="warehouse">Warehouse</option>
                  <option value="retail">Retail</option>
                  <option value="construction">Construction</option>
                  <option value="office">Office</option>
                  <option value="yard">Yard</option>
                  <option value="parking_lot">Parking Lot</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>
                <span className="text-xs font-medium text-slate-600">Coverage type</span>
                <select
                  value={ctx.coverage_type}
                  onChange={(e) => setCtx({ ...ctx, coverage_type: e.target.value })}
                  className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                >
                  <option value="">—</option>
                  <option value="static">Static</option>
                  <option value="patrol">Patrol</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </label>
              <label>
                <span className="text-xs font-medium text-slate-600">Coverage hours</span>
                <select
                  value={ctx.coverage_hours}
                  onChange={(e) => setCtx({ ...ctx, coverage_hours: e.target.value })}
                  className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                >
                  <option value="">—</option>
                  <option value="8h">8h</option>
                  <option value="12h">12h</option>
                  <option value="16h">16h</option>
                  <option value="24h">24h</option>
                </select>
              </label>
              <label>
                <span className="text-xs font-medium text-slate-600">Patrol intensity</span>
                <select
                  value={ctx.patrol_intensity}
                  onChange={(e) => setCtx({ ...ctx, patrol_intensity: e.target.value })}
                  className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                >
                  <option value="">—</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </label>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={() => ctxSave.mutate()}
                disabled={ctxSave.isPending}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-brand-600"
              >
                {ctxSave.isPending ? 'Saving…' : 'Save Context'}
              </button>
              <button onClick={() => setStep(4)} className="text-sm text-slate-500 underline hover:text-slate-700">
                Skip for now
              </button>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900">Connect with a vendor</h1>
            <p className="mt-2 text-sm text-slate-600">
              Browse the directory to find vendors near you, or invite one
              by email if you already have someone in mind.
            </p>
            <div className="mt-6 space-y-3">
              <a href="/directory" target="_blank" rel="noopener noreferrer" className="block rounded-xl border border-slate-200 p-4 hover:bg-slate-50">
                <div className="text-sm font-semibold text-slate-800">Browse the vendor directory</div>
                <div className="text-xs text-slate-500">Opens in a new tab.</div>
              </a>
              <div className="block rounded-xl border border-slate-200 p-4">
                <div className="text-sm font-semibold text-slate-800">Invite a specific vendor</div>
                <div className="mt-2 text-xs text-slate-500">
                  Set this up from your dashboard after onboarding — invitations are part of the Connections workflow.
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button onClick={() => setStep(5)} className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600">
                Continue
              </button>
              <button onClick={() => setStep(5)} className="text-sm text-slate-500 underline hover:text-slate-700">
                Skip for now
              </button>
            </div>
          </>
        )}

        {step === 5 && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900">You're set up!</h1>
            <p className="mt-2 text-sm text-slate-600">
              From the dashboard you can add more sites, browse vendors,
              and review scores as data starts flowing in.
            </p>
            <div className="mt-6">
              <button
                onClick={() => finishMutation.mutate()}
                disabled={finishMutation.isPending}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-brand-600"
              >
                {finishMutation.isPending ? 'Saving…' : 'Go to dashboard'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
